/**
 * The video encoder: ffmpeg.wasm, self-hosted at /ffmpeg/.
 *
 * This is the only encoder the console has. lib/video.ts explains why FFmpeg
 * rather than WebCodecs — briefly, it is the same code on every machine and it
 * runs the agent's own command line, where a platform encoder made two event
 * PCs produce different files from the same session.
 *
 * The approach and the /ffmpeg/ hosting come from the sibling project
 * D:\Projects\RJI\photobooth-pro-app (`src/app/lib/ffmpegLoader.ts`).
 *
 * ── The 32MB, and why it is still not precached ──────────────────────────────
 *
 * `ffmpeg-core.wasm` is 32MB, and every machine needs it now rather than a few,
 * so precaching it is tempting. But the precache runs on FIRST LOAD of the
 * hosted URL — which is also how someone opens the page just to read the setup
 * screen. Spending 32MB of a venue connection before the operator has decided
 * anything is the wrong moment.
 *
 * So it stays out of the precache manifest (`globIgnores` in vite.config.ts),
 * is fetched deliberately, and is then held by a CacheFirst runtime rule so a
 * machine pays exactly once. Settings offers it with a download button and
 * progress, and the alternative is a 32MB download starting with a guest at
 * the booth.
 *
 * It is now a REQUIREMENT rather than a warning — step 1 will not start
 * watching until the core is cached — but only while `videoEnabled` is on. An
 * operator who has turned the animation off is not asked to download an encoder
 * for a file they will never produce. See lib/capabilities.ts.
 *
 * ── Why the UMD bundle is loaded via a script tag ────────────────────────────
 *
 * @ffmpeg/ffmpeg's UMD build finds its worker by appending "814.ffmpeg.js" to
 * the directory of the currently executing script. Importing it through the
 * bundler puts it in /assets/, where the worker is not, and the failure appears
 * only at run time. A plain <script src="/ffmpeg/ffmpeg.js"> keeps
 * `document.currentScript.src` pointing at /ffmpeg/ so the calculation holds.
 */

import type { FFmpeg } from '@ffmpeg/ffmpeg'

export type FfmpegStatus = 'idle' | 'loading' | 'ready' | 'error'

export interface FfmpegState {
  status: FfmpegStatus
  /** 0–1 download progress for the wasm core. 0 when content-length is absent. */
  progress: number
  error?: string
}

const BASE = '/ffmpeg'

let instance: FFmpeg | null = null
let loading: Promise<FFmpeg> | null = null
let state: FfmpegState = { status: 'idle', progress: 0 }

const listeners = new Set<(state: FfmpegState) => void>()

function emit(next: FfmpegState): void {
  state = next
  for (const listener of listeners) listener({ ...next })
}

export function subscribeFfmpeg(listener: (state: FfmpegState) => void): () => void {
  listeners.add(listener)
  listener({ ...state })
  return () => {
    listeners.delete(listener)
  }
}

export function ffmpegState(): FfmpegState {
  return { ...state }
}

/**
 * Whether the encoder is usable without a download — loaded already, or sitting
 * in the service worker cache from a previous evening.
 */
export async function encoderReady(): Promise<boolean> {
  if (instance) return true
  if (typeof caches === 'undefined') return false
  try {
    return Boolean(await caches.match(`${BASE}/ffmpeg-core.wasm`))
  } catch {
    return false
  }
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.src = src
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`Tidak bisa memuat ${src}`))
    document.head.appendChild(script)
  })
}

/** Fetches a URL to a blob: URL, reporting progress as it streams. */
async function fetchBlobUrl(
  url: string,
  mime: string,
  onProgress?: (fraction: number) => void,
): Promise<string> {
  const response = await fetch(url)
  if (!response.ok) throw new Error(`HTTP ${response.status} untuk ${url}`)

  const total = Number(response.headers.get('content-length') ?? 0)
  const body = response.body
  // No streaming body (an older browser, or a cache hit without one): take it
  // whole rather than failing — progress just stays at 0.
  if (!body) return URL.createObjectURL(new Blob([await response.arrayBuffer()], { type: mime }))

  const reader = body.getReader()
  const chunks: Uint8Array[] = []
  let received = 0

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    chunks.push(value)
    received += value.length
    if (total > 0) onProgress?.(received / total)
  }

  return URL.createObjectURL(new Blob(chunks as BlobPart[], { type: mime }))
}

/**
 * Downloads and initialises ffmpeg.wasm. Idempotent and safe to call from
 * several places at once — the second caller waits on the first's download
 * rather than starting a second 32MB fetch.
 */
export function loadFfmpeg(): Promise<FFmpeg> {
  if (loading) return loading

  emit({ status: 'loading', progress: 0 })

  loading = (async () => {
    try {
      await loadScript(`${BASE}/ffmpeg.js`)
      const globals = window as unknown as { FFmpegWASM?: { FFmpeg: new () => FFmpeg } }
      const FFmpegClass = globals.FFmpegWASM?.FFmpeg
      if (!FFmpegClass) throw new Error('Bundle ffmpeg tidak memuat FFmpegWASM')

      // The shim is ~110KB; only the core is worth a progress bar.
      const coreURL = await fetchBlobUrl(`${BASE}/ffmpeg-core.js`, 'text/javascript')
      const wasmURL = await fetchBlobUrl(`${BASE}/ffmpeg-core.wasm`, 'application/wasm', (p) =>
        emit({ status: 'loading', progress: p }),
      )

      emit({ status: 'loading', progress: 1 })

      const ffmpeg = new FFmpegClass()
      await ffmpeg.load({ coreURL, wasmURL })

      instance = ffmpeg
      emit({ status: 'ready', progress: 1 })
      return ffmpeg
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      emit({ status: 'error', progress: 0, error: message })
      // Allow a retry: a venue connection that dropped mid-download should not
      // leave the encoder permanently unavailable for the rest of the evening.
      loading = null
      throw error
    }
  })()

  return loading
}

/** Downloads the encoder without using it. Wired to the setup and Settings buttons. */
export async function preload(): Promise<void> {
  await loadFfmpeg()
}
